import { useDispatch } from 'react-redux'
import { Badge, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap'
import images from '../../../api/images/hooks'
import settings from '../../../api/settings/hooks'
import noPhoto from '../../../assets/images/no-photo.jpg'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../../common/constants'
import { projectQuickViewData } from '../../../common/views/projectViewData'
import { propertyQuickViewData } from '../../../common/views/propertyViewData'
import { hideModal } from '../../../store/modal'
import { useBlobUrls } from '../../Hooks/useBlobUrl'
import { isPropertyData, ModuleType, ProjectQuickViewData, PropertyQuickViewData, QuickViewModalBodyData, QuickViewModalProps } from './types'

const QuickViewModal = (props: QuickViewModalProps) => {
    const dispatch = useDispatch()

    const { isOpen, quickViewModal } = props
    const { data, appModule } = quickViewModal

    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const appSettings = {
        currency: APPLICATION_SETTINGS.CURRENCY,
        areaUnit: APPLICATION_SETTINGS.AREA_UNIT,
    }

    const [imageQuery] = images.GetImagesByUrl.useQueries(data?.mainPhoto ? [data.mainPhoto] : [], '')
    const blobUrl = useBlobUrls([imageQuery])

    const isLoading = imageQuery?.isPending
    const src = blobUrl[0]

    const { data: applicationMarket } = settings.GetApplicationSettings.useQuery({
        select: (data) => data.applicationMarket,
    })

    // TODO: probably could be removed since RS market will be non-active 
    const IS_RS_MARKET = applicationMarket?.name === 'Srbija'

    const renderQuickViewModalBody = (
        data: ProjectQuickViewData | PropertyQuickViewData,
        appModule: ModuleType,
        appSettings: Record<string, string>
    ) => {
        const dataTypes = {
            project: () => projectQuickViewData(data as ProjectQuickViewData),
            property: () => propertyQuickViewData(data as PropertyQuickViewData, appSettings),
        }

        const bodyData: QuickViewModalBodyData[] = dataTypes[appModule]()

        return bodyData.map((obj) => {
            if (!obj.value) return

            return (
                <Row className={`align-items-start mb-4`} key={obj?.id}>
                    <Col sm={6}>
                        <span className="flex-grow-1 fw-medium">{obj?.label}:</span>
                    </Col>
                    <Col sm={6}>
                        <span className="flex-grow-1 fw-light">{obj.value}</span>
                    </Col>
                </Row>
            )
        })
    }

    const renderNewConstructionBadge = () => {
        if (!isPropertyData(data)) return null

        const newConstruction = data.propertyCondition?.find((condition) => condition.id === 1)

        if (newConstruction) {
            return (
                <Badge color="edit" className="fs-12 fw-medium me-3">
                    {newConstruction.name?.toUpperCase()}
                </Badge>
            )
        }
    }

    const renderLuxBadge = () => {
        if (!isPropertyData(data)) return null

        const lux = data.propertyDescriptions?.find((description) => description.id === 40)

        if (lux) {
            return (
                <Badge color="edit" className="fs-12 fw-medium me-3">
                    {lux.name?.toUpperCase()}
                </Badge>
            )
        }
    }

    const renderLandRegistryBadge = () => {
        if (!isPropertyData(data)) return null

        const landRegistry = data.propertyTransaction?.propertyLandRegistryStatus

        return (
            IS_RS_MARKET &&
            landRegistry?.id === 6 && (
                <Badge color="warning" className="fs-12 fw-medium me-3">
                    {landRegistry?.name?.toUpperCase()}
                </Badge>
            )
        )
    }

    const getModalTitle = () => {
        const fallbackTitle = (
            <span className="h5">
                {data?.street ? `${data.street} ${data?.streetNumber ?? ''}, ` : ''} {data?.city ? `${data.city?.name}, ` : ''}{' '}
                {data?.country ? data.country?.name : ''}
            </span>
        )

        if (isPropertyData(data)) {
            return data?.translations?.[currentLang]?.propertyTitle ? (
                <span className="h5">{data.translations[currentLang].propertyTitle}</span>
            ) : (
                fallbackTitle
            )
        } else {
            // Project modal title
            return data?.translations?.[currentLang]?.projectTitle ? (
                <span className="h5">{data.translations[currentLang].projectTitle}</span>
            ) : (
                fallbackTitle
            )
        }
    }

    const getImageAlt = () => {
        if (isPropertyData(data)) {
            return data?.translations?.[currentLang]?.propertyTitle || 'Property'
        }
        return data?.translations?.[currentLang]?.projectTitle || 'Project'
    }

    const handleClose = () => dispatch(hideModal({ modalType: MODAL_TYPES.QUICK_VIEW_MODAL }))

    return (
        <Modal size="lg" isOpen={isOpen} centered>
            <ModalHeader toggle={handleClose}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Row className="mt-4">
                    <Col sm={12} md={6}>
                        {renderQuickViewModalBody(data, appModule, appSettings)}
                    </Col>
                    <Col sm={12} md={6}>
                        <Row>
                            <Col>
                                {isLoading ? (
                                    <img src={noPhoto} alt={getImageAlt()} className="img-fluid" />
                                ) : (
                                    <img src={src || noPhoto} alt={getImageAlt()} className="img-fluid" />
                                )}
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col>
                                {renderNewConstructionBadge()}
                                {renderLuxBadge()}
                                {renderLandRegistryBadge()}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
}

export default QuickViewModal
